import Constants from './Constants';

const dateFields = ['_SD', '_ED', '_UD'];

/**
 * Builds a param object for http get calls.
 * Options:
 * {String} searchText Search text.
 * {String} searchAt Search text at this location/position.
 * {Object} pager Table pager object.
 * {Object} tableOptions Vuetify table options for sorting.
 * {Array} searchFields Array of fields that can be searched on.
 * {Number} viewStatusId Record status to add to the filter, if any.
 * {String} projectId Project for filtering.
 *
 * @param {Object} opt Options to build the parameters with.
 */
function buildGetParams (opt) {
    if (!opt) return;
    if (typeof opt.searchFields === 'string') opt.searchFields = [opt.searchFields];
    const status = Constants.STATUS.find(o => o.id === opt.viewStatusId);
    const statusId = status ? status.id : null;
    const text = opt.searchText ? opt.searchText.trim() : '';
    // Pager as is.
    const parms = {
        paging: opt.pager,
        sort: {}
    };
    // Use the table options to build the sorting.
    if (opt.tableOptions) {
        opt.tableOptions.sortBy.forEach((o, i) => {
            parms.sort[o] = opt.tableOptions.sortDesc[i] ? -1 : 1;
        });
    }
    // Set the status filter.
    parms.where = opt.where || {};
    if (statusId) {
        parms.where.StatusId = statusId;
    }
    if (opt.projectId) {
        parms.where.ProjectId = opt.projectId;
    }
    // Set the search filter.
    if (text.length) {
        if (!parms.where.$or) parms.where.$or = [];
        const orList = parms.where.$or;
        opt.searchFields.forEach(f => {
            const orf = {};
            switch (opt.searchAt.value) {
                case 'start': orf[f] = `/s:${text}`; break;
                case 'end': orf[f] = `/e:${text}`; break;
                case 'any': orf[f] = `/a:${text}`; break;
                case 'exact': orf[f] = `/x:${text}`; break;
                case 'not': orf[f] = `/!:${text}`; break;
                default: orf[f] = text; break;
            }
            orList.push(orf);
        });
    }
    return { params: parms };
}

/**
 * Builds a mongo friendly query object.
 *
 * @param {Object} query Query object.
 * @param {Array} filters List of filters to apply.
 */
function buildQueryWithFilters (query, filters) {
    for (const item of filters) {
        let value = item.Value;
        let value2;
        if (dateFields.indexOf(item.Field) > -1) {
            if (item.Oper.value === '&') {
                value = new Date(value[0]);
                value2 = new Date(value[1]);
            }
            else if (item.Oper.value === '[]') {
                for (let i = 0; i < value.length; i++) {
                    value[i] = new Date(value[i]);
                }
            }
            else value = new Date(value);
        }
        switch (item.Oper.value) {
            case 'start': query[item.Field] = `/s:${value}`; break; // String only.
            case 'end': query[item.Field] = `/e:${value}`; break; // String only.
            case 'any': query[item.Field] = `/a:${value}`; break; // String only.
            case 'exact': query[item.Field] = `/x:${value}`; break; // String only.
            case 'not': query[item.Field] = `/!:${value}`; break; // String only.
            case '=': query[item.Field] = value; break;
            case '!=': query[item.Field] = { $ne: value }; break;
            case '>': query[item.Field] = { $gt: value }; break;
            case '<': query[item.Field] = { $lt: value }; break;
            case '>=': query[item.Field] = { $gte: value }; break;
            case '<=': query[item.Field] = { $lte: value }; break;
            case '[]': query[item.Field] = { $in: value }; break;
            case '&': query[item.Field] = { $gte: value, $lte: value2 }; break;
        }
    }
}

/**
 * Counts the occurrance of every itemin the array.
 * Returns a map e.g. Map(6) { 1 → 2, 2 → 4, 3 → 1, 4 → 1, 55 → 1, 5 → 3 }
 *
 * @param {Array} arr Array with basic item types.
 */
function countDistinctItems (arr) {
    return arr.reduce((countsMap, item) => countsMap.set(item, countsMap.get(item) + 1 || 1), new Map());
}

/**
 * Example: extend({}, objA, objB);
 *
 * @export
 * @param {any} out Extended object.
 * @returns Extended object.
 */
function extend (...args) {
    const out = args[0] || {};
    for (let i = 1; i < args.length; i++) {
        if (!args[i]) continue;
        const keys = Object.keys(args[i]);
        for (let ki = 0; ki < keys.length; ki++) {
            const key = keys[ki];
            out[key] = args[i][key];
        }
    }
    return out;
}

/**
 * Get the contrasting color for any hex color.
 * (c) 2019 Chris Ferdinandi, MIT License, https://gomakethings.com
 * Derived from work by Brian Suda, https://24ways.org/2010/calculating-color-contrast/
 *
 * @param  {String} A hexcolor value
 * @return {String} The contrasting color (black or white)
 */
function getContrast (hexcolor) {
    // If a leading # is provided, remove it
    if (hexcolor.slice(0, 1) === '#') {
        hexcolor = hexcolor.slice(1);
    }

    // If a three-character hexcode, make six-character
    if (hexcolor.length === 3) {
        hexcolor = hexcolor.split('').map(function (hex) {
            return hex + hex;
        }).join('');
    }

    // Convert to RGB value
    const r = parseInt(hexcolor.substr(0, 2), 16);
    const g = parseInt(hexcolor.substr(2, 2), 16);
    const b = parseInt(hexcolor.substr(4, 2), 16);

    // Get YIQ ratio
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

    // Check contrast
    return (yiq >= 128) ? 'black' : 'white';
};

/**
 * Used to extend the user object for permission checking.
 *
 * @param {*} cid Company ID
 * @param {*} permissions Permission id(s)
 */
function has (permissions) {
    if (typeof this !== 'object') return false;
    if (!Array.isArray(permissions)) permissions = [permissions]; // Make array if not.
    for (let i = 0; i < permissions.length; i++) { // Check each role.
        if (this.Perms.indexOf(permissions[i]) > -1) {
            return true; // Return on first find.
        }
    }
    return false; // None found.
}

/**
 * Converts a string (project or survey name etc.) into a valid table name.
 *
 * @param {String} name Name to convert to a safe table name.
 */
function nameToTableShort (name) {
    const parts = name.toUpperCase().split(/[ ,+-\/\_]+/); // eslint-disable-line no-useless-escape
    return parts.length === 1
        ? parts[0].substr(0, 10)
        : parts.map(o => o.substr(0, 3)).join(''); // Short first part of the word.
}

/**
 * Returns a string showing the first value and the count of undisplayed items.
 *
 * @param {Array} list List of values.
 * @param {String} field If the list contains objects, the field to use on the object.
 * @returns String
 */
function oneAndMore (list, field) {
    const maxChars = 15;
    const len = list.length;
    //
    if (len < 11) {
        const test = list.join(', ');
        if (test.length < 20) return test;
    }
    //
    let first = len
        ? (field ? list[0][field] : list[0]) + ''
        : '';
    if (first.length > maxChars) first = `${first.substring(0, maxChars)}...`;
    return !len
        ? ''
        : (len === 1
            ? first
            : `${first} (+${len - 1} more)`);
}

function searchFilterAt (text, fields, arr, searchAt) {
    if (typeof fields === 'string') fields = [fields];
    text = text.toLowerCase();
    return arr.filter(o => {
        // const v = `${field ? o[field] : o}`.toLocaleLowerCase();
        let match = false;
        switch (searchAt) {
            case 'start': {
                // return v.toLowerCase().startsWith(text);
                fields.forEach(f => {
                    if (`${f ? o[f] : o}`.toLocaleLowerCase().startsWith(text)) match = true;
                });
                return match;
            }
            case 'end': {
                // return v.toLowerCase().endsWith(text);
                fields.forEach(f => {
                    if (`${f ? o[f] : o}`.toLocaleLowerCase().endsWith(text)) match = true;
                });
                return match;
            }
            case 'any': {
                // return v.toLowerCase().indexOf(text) > -1;
                fields.forEach(f => {
                    if (`${f ? o[f] : o}`.toLocaleLowerCase().indexOf(text) > -1) match = true;
                });
                return match;
            }
            case 'exact': {
                // return v.toLowerCase() === text;
                fields.forEach(f => {
                    if (`${f ? o[f] : o}`.toLocaleLowerCase() === text) match = true;
                });
                return match;
            }
            default: return false;
        }
    });
}

function sleep (m) {
    return new Promise(resolve => setTimeout(resolve, m));
}

/**
 * Separates a string by the given separator, but retains the separator in the result.
 *
 * @param {String} str Value to split.
 * @param {String} separator Separator to split by.
 * @param {String} method Method.
 */
function splitAndKeep (str, separator, method = 'seperate') {
    function splitAndKeep (str, separator, method = 'seperate') {
        if (method === 'seperate') {
            str = str.split(new RegExp(`(${separator})`, 'g'));
        }
        else if (method === 'infront') {
            str = str.split(new RegExp(`(?=${separator})`, 'g'));
        }
        else if (method === 'behind') {
            str = str.split(new RegExp(`(.*?${separator})`, 'g'));
            str = str.filter(el => el !== '');
        }
        return str;
    }
    if (Array.isArray(separator)) {
        let parts = splitAndKeep(str, separator[0], method);
        for (let i = 1; i < separator.length; i++) {
            const partsTemp = parts;
            parts = [];
            for (let p = 0; p < partsTemp.length; p++) {
                parts = parts.concat(splitAndKeep(partsTemp[p], separator[i], method));
            }
        }
        return parts;
    }
    else {
        return splitAndKeep(str, separator, method);
    }
};

function toTitleCase (str) {
    return str.replace(/\b\w+/g, s => s.charAt(0).toUpperCase() + s.substr(1).toLowerCase());
}

export default {
    buildGetParams,
    buildQueryWithFilters,
    countDistinctItems,
    extend,
    getContrast,
    has,
    nameToTableShort,
    oneAndMore,
    searchFilterAt,
    sleep,
    splitAndKeep,
    toTitleCase
};
